import { useState, useEffect, useMemo, useCallback } from "react";
import { dataProvider } from "data";
import { baseApiClient } from "data/modules/api/api.client";
import moment from "moment";
import {
  isInternationalTimezone,
  setNextStepsInLocalStorage,
} from "utils/AuthUtil";
import { DEFAULT_TIME_FORMAT_12 } from "constants/dateTime";
import { getWhatsNextInfo } from "features/Onboarding/modules/WhatsNext/utils/whatsNext.utils";
import { notification_color_keys, useNotifications } from "utils/hooks";
import { SOMETHING_WENT_WRONG } from "constants/displayStrings";
import {
  OFFER_TARGET_TYPE,
  OFFER_TYPE,
  LTO_FORM_KEYS,
  TIMER_TYPE,
  LTO_EDIT_SESSION_KEY,
  DEFAULT_EVERGREEN_DURATION_UNIT,
  DEFAULT_EVERGREEN_DURATION_VALUE,
  DEFAULT_EVERGREEN_START_EVENT,
  DEFAULT_THRESHOLD_UNIT,
  DEFAULT_VALUE_UNIT,
  DEFAULT_VIP_APPLY_BY,
  VALUE_UNIT,
} from "./constants";
import {
  OFFER_DIRECTION,
  TRIGGER_TYPE,
  getDefaultShowAtCheckout,
} from "./utils/unifiedOffer";
import {
  buildCreateOfferPayload,
  buildUpdateOfferMetadata,
} from "./utils/offerPayload";
import {
  mapDirectionFromBackend,
  mapTriggerFromBackend,
} from "./utils/offerContract";
import { OFFER_API_URLS } from "./useOfferApis";
import {
  buildEditInitialValues,
  compareDates,
  mergeDetailIntoRecord,
  normalizeListingRef,
  normalizePaymentPageRef,
} from "./utils/editPrefill";
import { emptyTimeTier } from "./utils/tierOps";
import { OFFERING_STATUSES } from "constants/schedule";
import constants from "constants/constants";

const useLimitedTimeOffer = ({
  allDynamicPagesData = [],
  infiniteListingOptions = [],
  directionPreset,
} = {}) => {
  const { notify } = useNotifications();

  // Utility function to determine when to show offering selector
  const shouldShowOfferingSelector = (targetType) => {
    return targetType === OFFER_TARGET_TYPE.SPECIFIC_OFFERING;
  };

  // Utility function to determine when to show dynamic link (payment page) selector
  const shouldShowDynamicLinkSelector = (targetType) => {
    return targetType === OFFER_TARGET_TYPE.SPECIFIC_PAYMENT_PAGE;
  };

  // Utility function to determine when to show price fields
  const shouldShowPriceFields = (values) => {
    if (values.target_type === OFFER_TARGET_TYPE.ALL_OFFERINGS) {
      return true;
    }
    return !!(
      values?.[LTO_FORM_KEYS.LISTINGS]?.length ||
      (values.target_type === OFFER_TARGET_TYPE.SPECIFIC_PAYMENT_PAGE &&
        values?.[LTO_FORM_KEYS.DYNAMIC_LINK_UUIDS]?.length)
    );
  };

  // Utility function to determine when to show international pricing
  const shouldShowInternationalPricing = (values) => {
    if (isEdit) return false;
    if (values.target_type === OFFER_TARGET_TYPE.SPECIFIC_PAYMENT_PAGE) {
      return false;
    }
    if (values.target_type === OFFER_TARGET_TYPE.ALL_OFFERINGS) {
      return true;
    }
    return (
      values.target_type === OFFER_TARGET_TYPE.SPECIFIC_OFFERING &&
      values?.[LTO_FORM_KEYS.LISTINGS]?.length > 0
    );
  };

  const [initialValues, setInitialValues] = useState(() => {
    const editRecordData = window.sessionStorage.getItem(LTO_EDIT_SESSION_KEY)
      ? JSON.parse(window.sessionStorage.getItem(LTO_EDIT_SESSION_KEY))
      : null;
    const presetDirection =
      directionPreset === OFFER_DIRECTION.SURGE
        ? OFFER_DIRECTION.SURGE
        : OFFER_DIRECTION.DISCOUNT;
    return {
      target_type:
        editRecordData?.target_type || OFFER_TARGET_TYPE.SPECIFIC_OFFERING,
      [LTO_FORM_KEYS.LISTINGS]: [],
      [LTO_FORM_KEYS.OFFER_TYPE]: OFFER_TYPE.AMOUNT,
      [LTO_FORM_KEYS.TIMER_TYPE]: TIMER_TYPE.FIXED,
      [LTO_FORM_KEYS.EVERGREEN_DURATION_VALUE]:
        DEFAULT_EVERGREEN_DURATION_VALUE,
      [LTO_FORM_KEYS.EVERGREEN_DURATION_UNIT]: DEFAULT_EVERGREEN_DURATION_UNIT,
      offerName: editRecordData?.title || "Flash Deal",
      INR: true,
      [LTO_FORM_KEYS.INR_PRICE]: editRecordData?.price || null,
      [LTO_FORM_KEYS.PERCENTAGE_VALUE]: null,
      [LTO_FORM_KEYS.USD_CHECKBOX]: !!editRecordData?.price_international,
      [LTO_FORM_KEYS.USD_PRICE]: editRecordData?.price_international || null,
      start_date: moment(new Date()).format("YYYY-MM-DD"),
      end_date: moment(new Date()).add("days", 7).format("YYYY-MM-DD"),
      start_date_international: moment(new Date()).format("YYYY-MM-DD"),
      end_date_international: moment(new Date())
        .add("days", 7)
        .format("YYYY-MM-DD"),
      start_time: moment().add(1, "minutes").format(DEFAULT_TIME_FORMAT_12),
      end_time: "11:59 pm",
      start_time_international: "09:00 am",
      end_time_international: "11:00 pm",
      region: editRecordData?.region ?? (isInternationalTimezone() ? 1 : 0),
      [LTO_FORM_KEYS.DYNAMIC_LINK_UUIDS]: [],
      // Unified offer fields (frontend-only; nested into metadata on submit).
      [LTO_FORM_KEYS.DIRECTION]:
        mapDirectionFromBackend(editRecordData?.direction) ||
        editRecordData?.metadata?.direction ||
        presetDirection,
      [LTO_FORM_KEYS.TRIGGER_TYPE]:
        editRecordData?.metadata?.trigger_type ||
        mapTriggerFromBackend(editRecordData?.trigger_type) ||
        TRIGGER_TYPE.SCHEDULE,
      [LTO_FORM_KEYS.TIERS]: editRecordData?.metadata?.tiers || [
        emptyTimeTier(),
      ],
      [LTO_FORM_KEYS.THRESHOLD_UNIT]:
        editRecordData?.metadata?.threshold_unit || DEFAULT_THRESHOLD_UNIT,
      // New offers default to Offer Type = Amount → value_unit flat (no UI;
      // Offer Type drives it). Edits keep the stored unit; the form effect
      // re-syncs it if it ever drifts from Offer Type. Payload keys unchanged.
      [LTO_FORM_KEYS.VALUE_UNIT]:
        editRecordData?.metadata?.value_unit ||
        (editRecordData ? DEFAULT_VALUE_UNIT : VALUE_UNIT.FLAT),
      [LTO_FORM_KEYS.SHOW_AT_CHECKOUT]:
        editRecordData?.show_at_checkout ??
        editRecordData?.metadata?.show_at_checkout ??
        getDefaultShowAtCheckout(presetDirection),
      [LTO_FORM_KEYS.SYNC_END_TO_START]:
        editRecordData?.metadata?.sync_end_to_start ?? false,
      [LTO_FORM_KEYS.EVERGREEN_START_EVENT]:
        editRecordData?.metadata?.evergreen_start_event ||
        DEFAULT_EVERGREEN_START_EVENT,
      [LTO_FORM_KEYS.VIP_APPLY_BY]:
        editRecordData?.metadata?.vip_apply_by || DEFAULT_VIP_APPLY_BY,
      [LTO_FORM_KEYS.VIP_WINDOWS]: editRecordData?.metadata?.vip_windows || [],
      [LTO_FORM_KEYS.VIP_START_DATE]:
        editRecordData?.metadata?.vip_start_date || "",
      [LTO_FORM_KEYS.VIP_END_DATE]:
        editRecordData?.metadata?.vip_end_date || "",
      [LTO_FORM_KEYS.BATCH_VALUES]:
        editRecordData?.metadata?.batch_values || [],
      bookingCount:
        editRecordData?.bookingCount ?? editRecordData?.total_bookings ?? 0,
    };
  });
  const [isEdit, setIsEdit] = useState(false);
  const [isEditLoading, setIsEditLoading] = useState(false);
  const [detailRecord, setDetailRecord] = useState(null);
  const [firstLimitedOffer, setFirstLimitedOffer] = useState(false);
  const [limitedOfferCreative, setLimitedOfferCreative] = useState(false);
  const [shareUrl, setShareUrl] = useState(null);
  const [listingUuid, setListingUuid] = useState("");
  const [offers, setOffers] = useState(0);
  const [selectedListing, setSelectedListing] = useState([]);

  const recordData = window.sessionStorage.getItem(LTO_EDIT_SESSION_KEY)
    ? JSON.parse(window.sessionStorage.getItem(LTO_EDIT_SESSION_KEY))
    : null;

  useEffect(() => {
    (async () => {
      try {
        const offerData = await dataProvider.custom_request(
          OFFER_API_URLS.list,
          "GET"
        );
        if (offerData.status === 200) {
          setOffers(offerData.data.offers.length);
        }
      } catch (err) {
        notify(
          err?.body?.message ?? SOMETHING_WENT_WRONG,
          notification_color_keys.error
        );
      }
    })();

    if (window.sessionStorage.getItem(LTO_EDIT_SESSION_KEY)) {
      setIsEdit(true);
      const sessionRecord = window.sessionStorage.getItem(LTO_EDIT_SESSION_KEY)
        ? JSON.parse(window.sessionStorage.getItem(LTO_EDIT_SESSION_KEY))
        : null;
      // Prefill instantly from the session row, then enrich with detail for
      // any keys the list row lacks (sync flag, locks, fresh trigger config).
      setInitialValues(
        buildEditInitialValues({
          recordData: sessionRecord,
          baseInitialValues: initialValues,
          dateConversion: DateCoversion,
        })
      );
      if (sessionRecord?.uuid) {
        setIsEditLoading(true);
        baseApiClient
          .get(OFFER_API_URLS.detail, {
            params: { offer_uid: sessionRecord.uuid },
          })
          .then((res) => {
            const merged = mergeDetailIntoRecord(
              sessionRecord,
              res?.data?.data
            );
            setDetailRecord(merged);
            setInitialValues(
              buildEditInitialValues({
                recordData: merged,
                baseInitialValues: initialValues,
                dateConversion: DateCoversion,
              })
            );
          })
          .catch(() => {
            // fall through with the session row — form stays usable
          })
          .finally(() => setIsEditLoading(false));
      }
    }
  }, []);

  const DateField = (dateConvert, onlyTime, payloadDate) => {
    const replaceUTC = onlyTime
      ? dateConvert
      : dateConvert?.replace("T", " ")?.replace("Z", "");
    let month;
    let day;
    let year;
    let time;
    if (!onlyTime) {
      const dateFormat = replaceUTC?.split(" ");
      const timeFormat = dateFormat && dateFormat[1]?.split(":");
      // Same null-datetime guard as DateCoversion (open-ended offers).
      if (!dateFormat?.[0] || !timeFormat) {
        return {
          date1: "",
          time: "",
        };
      }
      const date = new Date(dateFormat[0]);
      month = date.toLocaleString("default", { month: "short" });
      day = date.getDate();
      year = `${date.getFullYear()}`;

      let hours = timeFormat[0]; // gives the value in 24 hours format
      const AmOrPm = hours >= 12 ? "PM" : "AM";
      hours = hours % 12 || 12;
      let minutes = timeFormat[1];
      time =
        (hours < 10 ? `0${hours}` : hours) +
        ":" +
        (minutes < 10 ? `${minutes}` : minutes) +
        " " +
        AmOrPm;
    } else {
      const date = new Date(replaceUTC);
      month = payloadDate
        ? `${date.getMonth() < 9 ? 0 : ""}${date.getMonth() + 1}`
        : date.getMonth();
      day = date.getDate();
      year = date.getFullYear();
      let hours = date.getHours(); // gives the value in 24 hours format
      const AmOrPm = hours >= 12 ? "PM" : "AM";
      hours = hours % 12 || 12;
      let minutes = date.getMinutes();
      time =
        (hours < 10 ? `0${hours}` : hours) +
        ":" +
        (minutes < 10 ? `0${minutes}` : minutes) +
        " " +
        AmOrPm;
    }

    let date1 = `${year}-${month}-${day}`;
    return {
      date1,
      time,
    };
  };

  const DateCoversion = (entereddate) => {
    const replaceUTC = entereddate?.replace("T", " ")?.replace("Z", "");
    const dateFormat = replaceUTC?.split(" ");
    const timeFormat = dateFormat && dateFormat[1]?.split(":");
    // List rows can carry null datetimes (open-ended / evergreen offers).
    // Return blanks instead of throwing on dateFormat[0]/timeFormat[0].
    if (!dateFormat?.[0] || !timeFormat) {
      return {
        time: "",
        date: "",
      };
    }
    const date = new Date(dateFormat[0]);
    let hours = timeFormat[0]; // gives the value in 24 hours format
    const AmOrPm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12;
    let minutes = timeFormat[1];
    let time =
      (hours < 10 ? `0${hours}` : hours) +
      ":" +
      (minutes < 10 ? `${minutes}` : minutes) +
      " " +
      AmOrPm;

    return {
      time,
      date,
    };
  };

  const handleCreateOffer = async (formState) => {
    const isPercentage =
      formState[LTO_FORM_KEYS.OFFER_TYPE] === OFFER_TYPE.PERCENTAGE;

    const payload = buildCreateOfferPayload(formState);

    if (window.sessionStorage.getItem(LTO_EDIT_SESSION_KEY)) {
      const recordData = window.sessionStorage.getItem(LTO_EDIT_SESSION_KEY)
        ? JSON.parse(window.sessionStorage.getItem(LTO_EDIT_SESSION_KEY))
        : null;
      const isPercentageEdit =
        formState[LTO_FORM_KEYS.OFFER_TYPE] === OFFER_TYPE.PERCENTAGE;
      // timer_type is prefilled correctly on edit (evergreen only for the
      // evergreen trigger; threshold triggers are forced to fixed).
      const isEvergreenEdit =
        formState[LTO_FORM_KEYS.TIMER_TYPE] === TIMER_TYPE.EVERGREEN;

      const editPayload = {
        offer_uid: recordData.uuid,
        // offering + trigger structure is locked after create; only values travel.
        updated_values: {
          ...buildUpdateOfferMetadata(formState),
          offer_type: formState[LTO_FORM_KEYS.OFFER_TYPE],
        },
      };

      if (isEvergreenEdit) {
        editPayload.updated_values.evergreen_duration_value =
          formState[LTO_FORM_KEYS.EVERGREEN_DURATION_VALUE];
        editPayload.updated_values.evergreen_duration_unit =
          formState[LTO_FORM_KEYS.EVERGREEN_DURATION_UNIT];
        // Backend allows changing the start event on evergreen offers.
        if (formState[LTO_FORM_KEYS.EVERGREEN_START_EVENT]) {
          editPayload.updated_values.evergreen_start_event =
            formState[LTO_FORM_KEYS.EVERGREEN_START_EVENT];
        }
      } else {
        if (
          !compareDates(
            DateCoversion(recordData.end_datetime).date,
            formState.end_date
          ) ||
          DateCoversion(recordData.end_datetime).time !== formState.end_time
        ) {
          editPayload.updated_values.end_date =
            DateField(recordData.end_datetime).time !== formState.end_time
              ? DateField(formState.end_date, true, true).date1
              : formState.end_date;
          editPayload.updated_values.end_time = formState.end_time;
        }
        if (
          !compareDates(
            DateCoversion(recordData.start_datetime).date,
            formState.start_date
          ) ||
          DateCoversion(recordData.start_datetime).time !== formState.start_time
        ) {
          editPayload.updated_values.start_date =
            DateField(recordData.start_datetime).time !== formState.start_time
              ? DateField(formState.start_date, true, true).date1
              : formState.start_date;
          editPayload.updated_values.start_time = formState.start_time;
        }
      }

      // Price keys travel only with real values — never as null.
      if (isPercentageEdit) {
        const pct = parseFloat(formState[LTO_FORM_KEYS.PERCENTAGE_VALUE]);
        if (Number.isFinite(pct)) {
          editPayload.updated_values.discount_percentage = pct;
        }
      } else {
        const inr = parseFloat(formState[LTO_FORM_KEYS.INR_PRICE]);
        if (Number.isFinite(inr)) {
          const priceValue = inr.toFixed(2);
          if (recordData?.region === constants.domestic_region) {
            editPayload.updated_values.price_international = priceValue;
          } else {
            editPayload.updated_values.price_inr = priceValue;
          }
        }
      }

      if (formState.target_type === OFFER_TARGET_TYPE.SPECIFIC_PAYMENT_PAGE) {
        editPayload.dynamic_link_uuids =
          formState[LTO_FORM_KEYS.DYNAMIC_LINK_UUIDS] || [];
      }

      if (!isPercentageEdit && formState[LTO_FORM_KEYS.USD_CHECKBOX]) {
        const usd = parseFloat(formState[LTO_FORM_KEYS.USD_PRICE]);
        if (Number.isFinite(usd)) {
          editPayload.updated_values.price_international = usd.toFixed(2);
        }
      }
      try {
        const data = await dataProvider.custom_request(
          OFFER_API_URLS.update,
          "POST",
          editPayload
        );
        if (data.status === 200) {
          window.sessionStorage.removeItem(LTO_EDIT_SESSION_KEY);
          notify(
            "Offer updated successfully!",
            notification_color_keys.success
          );
          return {
            listingOfferData: { updated: true },
            limitedOfferCreative: false,
          };
        }
      } catch (err) {
        notify(
          err?.body?.message ?? SOMETHING_WENT_WRONG,
          notification_color_keys.error
        );
      }
    } else {
      try {
        const data = await dataProvider.custom_request(
          OFFER_API_URLS.create,
          "POST",
          payload
        );
        if (data.status === 200) {
          const domesticOffer = data?.data?.domestic_offer || {};
          const internationalOffer = data?.data?.international_offer || {};
          const isSingleTarget = domesticOffer.is_single_target !== false;
          // Threshold/time/batch triggers don't carry start/end datetimes, so
          // the create API returns no offer row (and therefore no share_url)
          // for them. Fall back to the selected offering's own share link so
          // the success share popup still opens for every trigger type.
          const selectedListingOption = infiniteListingOptions.find(
            (option) =>
              payload.listing_uids?.includes(option.uuid || option.value)
          );
          const listingShareUrl =
            selectedListingOption?.share_url ||
            selectedListingOption?.metadata?.share_url ||
            null;
          const listingFallback = selectedListingOption
            ? {
                ...selectedListingOption,
                share_url: listingShareUrl,
                listing_name:
                  selectedListingOption.listing_name ||
                  selectedListingOption.title ||
                  selectedListingOption.label,
              }
            : null;
          const shareUrl =
            domesticOffer.share_url ||
            internationalOffer.share_url ||
            listingShareUrl;
          const shouldShowModal = isSingleTarget && !!shareUrl;
          let showCongrats = false;

          if (shareUrl) {
            setShareUrl(shareUrl);
          }

          if (shouldShowModal) {
            const offerData = await dataProvider.custom_request(
              OFFER_API_URLS.list,
              "GET"
            );
            const currentListing = offerData.data.offers.filter(
              (offer) =>
                (payload.listing_uids?.includes(offer.listing_uuid) &&
                  offer.status != OFFERING_STATUSES.soldout.value) ||
                offer?.uuid === domesticOffer?.uuid
            );
            setListingUuid(payload?.listing_uids?.[0] || payload?.uuid);
            setLimitedOfferCreative(true);
            // Prefer the fresh offer row, but fall back to the selected
            // offering when the create response omitted offer details (the
            // share card only needs the listing title + share link).
            setSelectedListing(currentListing?.[0] || listingFallback);
            showCongrats = true;
          }

          notify("Offer Created Successfully", notification_color_keys.success);
          const nextSteps = getWhatsNextInfo();
          if (nextSteps) {
            nextSteps.has_discount_code = true;
            setNextStepsInLocalStorage({ data: nextSteps });
          }
          if (offers === 0) {
            setFirstLimitedOffer(true);
            return { firstLimitedOffer: true };
          }

          return {
            listingOfferData: {
              region: initialValues.region,
              listing_uuid: payload.listing_uids?.[0],
              offer_type: payload.offer_type,
              share_url: shareUrl,
              ...(isPercentage
                ? { discount_percentage: payload.discount_percentage }
                : { price_inr: payload.price_inr }),
              ...(payload.price_international
                ? internationalOffer
                : domesticOffer),
            },
            limitedOfferCreative: showCongrats,
          };
        }
      } catch (err) {
        notify(
          err?.body?.message ?? SOMETHING_WENT_WRONG,
          notification_color_keys.error
        );
      }
    }
  };

  const listingTitleMap = useMemo(() => {
    if (!Array.isArray(recordData?.listing_uids)) return {};
    const map = {};
    recordData.listing_uids.forEach((l) => {
      if (typeof l === "string") {
        map[l] = l;
        return;
      }
      const { uuid, title } = normalizeListingRef(l);
      if (uuid) map[uuid] = title || uuid;
    });
    return map;
  }, [recordData?.listing_uids]);

  const getEditOfferingFallback = useCallback(
    (infiniteOptions) => {
      if (!isEdit || !initialValues[LTO_FORM_KEYS.LISTINGS]?.length)
        return infiniteOptions;
      const selectedUuids = initialValues[LTO_FORM_KEYS.LISTINGS];
      const merged = [...(infiniteOptions || [])];
      selectedUuids.forEach((uuid) => {
        if (!merged.find((o) => o.uuid === uuid || o.value === uuid)) {
          merged.push({
            uuid,
            id: uuid,
            value: uuid,
            label: listingTitleMap[uuid] || uuid,
          });
        }
      });
      return merged;
    },
    [isEdit, initialValues, listingTitleMap]
  );

  const comboPagesOptions = useMemo(() => {
    const currComboPages = initialValues.combo_pages;
    if (isEdit && currComboPages && currComboPages.length > 0) {
      return currComboPages.map((comboPage) => ({
        ...comboPage,
        id: comboPage.dynamic_link_id || comboPage.uuid,
        label: comboPage.title,
        value: comboPage.dynamic_link_id || comboPage.uuid,
      }));
    }
    return [];
  }, [initialValues.combo_pages, isEdit]);

  const dynamicLinkTitleMap = useMemo(() => {
    if (!Array.isArray(recordData?.dynamic_link_uuids)) return {};
    const map = {};
    recordData.dynamic_link_uuids.forEach((d) => {
      if (typeof d === "string") {
        map[d] = d;
        return;
      }
      const { dynamic_link_id: id, title } = normalizePaymentPageRef(d);
      if (id) map[id] = title || id;
    });
    return map;
  }, [recordData?.dynamic_link_uuids]);

  const dynamicPagesOptions = useMemo(() => {
    if (!isEdit || !initialValues[LTO_FORM_KEYS.DYNAMIC_LINK_UUIDS]?.length)
      return allDynamicPagesData;
    const options = [...(allDynamicPagesData || [])];
    initialValues[LTO_FORM_KEYS.DYNAMIC_LINK_UUIDS].forEach((id) => {
      if (
        !options.find(
          (o) => o.dynamic_link_id === id || o.uuid === id || o.value === id
        )
      ) {
        const title = dynamicLinkTitleMap[id] || id;
        options.push({
          dynamic_link_id: id,
          uuid: id,
          id,
          value: id,
          label: title,
          title,
        });
      }
    });
    return options;
  }, [allDynamicPagesData, isEdit, initialValues, dynamicLinkTitleMap]);

  const offeringOptions = useMemo(
    () => getEditOfferingFallback(infiniteListingOptions),
    [infiniteListingOptions, getEditOfferingFallback]
  );

  return {
    comboPagesOptions,
    recordData: detailRecord || recordData,
    handleCreateOffer,
    isEdit,
    isEditLoading,
    initialValues,
    DateField,
    firstLimitedOffer,
    limitedOfferCreative,
    listingUuid,
    selectedListing,
    shareUrl,
    getEditOfferingFallback,
    shouldShowOfferingSelector,
    shouldShowDynamicLinkSelector,
    shouldShowPriceFields,
    shouldShowInternationalPricing,
    dynamicLinkTitleMap,
    dynamicPagesOptions,
    offeringOptions,
  };
};

export { useLimitedTimeOffer };
